import { useState, useEffect, useRef } from "react";
import styles from "./AboutSection.module.css";
import aboutImg from "../../assets/img/services/about.jpg";

/* ────────────────────────────────────────────
   ABOUT SECTION  —  iGiTREE
   ──────────────────────────────────────────── */

/* ── Testimonials data ── */
interface Testimonial {
  id: number;
  quote: string;
  name: string;
  role: string;
  gender: "female" | "male";
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    quote:
      "Being a survivor of the Rwanda Genocide Against the Tutsi, I had little hope of ever finding my lost family members. iGiTREE's mission to reunite families became my beacon of light, and I am eternally grateful for their unwavering support.",
    name: "Anonymous",
    role: "Genocide Survivor, Rwanda",
    gender: "female",
  },
  {
    id: 2,
    quote:
      "iGiTREE's mission to promote family connectivity goes beyond borders. As an immigrant in a new land, their platform helped me build bridges to my roots, ensuring my children know where they come from. It's a gift that keeps giving.",
    name: "Anonymous",
    role: "Immigrant, South Africa",
    gender: "male",
  },
  {
    id: 3,
    quote:
      "iGiTREE has been a beacon of hope in my life. As a Rwandan refugee, I had lost all contact with my family. Thanks to their platform, I found my siblings after 15 years of separation. I can't express my gratitude enough!",
    name: "Anonymous",
    role: "Rwandan Refugee",
    gender: "male",
  },
];

/* ── Avatar SVG ── */
function Avatar({ gender }: { gender: "female" | "male" }) {
  return (
    <svg
      viewBox="0 0 56 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.avatarSvg}
    >
      <circle cx="28" cy="28" r="28" fill="#E8EEF8" />
      <circle cx="28" cy="22" r="10" fill="#94A3B8" />
      <ellipse cx="28" cy="46" rx="16" ry="10" fill="#94A3B8" />
      {gender === "female" && (
        <path d="M22 19 Q28 14 34 19" stroke="#CBD5E1" strokeWidth="1.5" fill="none" />
      )}
    </svg>
  );
}

/* ── Testimonial card ── */
function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <div className={styles.testimonialCard}>
      <div className={styles.quoteIcon}>"</div>
      <p className={styles.testimonialQuote}>{testimonial.quote}</p>
      <div className={styles.testimonialAuthor}>
        <Avatar gender={testimonial.gender} />
        <div>
          <p className={styles.authorName}>{testimonial.name}</p>
          <p className={styles.authorRole}>{testimonial.role}</p>
        </div>
      </div>
    </div>
  );
}

/* ── Testimonials slider ── */
function TestimonialsSlider() {
  const [active, setActive] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials.length);
    }, 5000);
  };

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const goTo = (idx: number) => {
    setActive(idx);
    if (timerRef.current) clearInterval(timerRef.current);
    startTimer();
  };

  return (
    <div className={styles.testimonialsWrapper}>
      <div className={styles.testimonialsHeading}>
        <p className={styles.testimonialsEyebrow}>Stories</p>
        <h2 className={styles.testimonialsTitle}>
          What People Say About{" "}
          <em className={styles.testimonialsTitleEm}>iGiTREE</em>
        </h2>
        <div className={styles.testimonialsDivider}>
          <span className={styles.testimonialsDividerLong} />
          <span className={styles.testimonialsDividerShort} />
        </div>
      </div>

      {/* Desktop: show all 3 */}
      <div className={styles.testimonialsGrid}>
        {testimonials.map((t) => (
          <TestimonialCard key={t.id} testimonial={t} />
        ))}
      </div>

      {/* Mobile: show one at a time */}
      <div className={styles.testimonialsMobile}>
        <TestimonialCard testimonial={testimonials[active]} />
        <div className={styles.dots}>
          {testimonials.map((_, i) => (
            <button
              key={i}
              className={`${styles.dot} ${i === active ? styles.dotActive : ""}`}
              onClick={() => goTo(i)}
              aria-label={`Go to testimonial ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Desktop dots */}
      <div className={`${styles.dots} ${styles.dotsDesktop}`}>
        {testimonials.map((_, i) => (
          <button
            key={i}
            className={`${styles.dot} ${i === active ? styles.dotActive : ""}`}
            onClick={() => goTo(i)}
            aria-label={`Highlight testimonial ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

/* ── Main exported section ── */
export default function AboutSection() {
  return (
    <div className={styles.aboutWrapper} id="about">
      {/* ── Vision / Mission ── */}
      <section className={styles.visionSection}>
        <div className={styles.decoBlobTop} aria-hidden />
        <div className={styles.decoSquareAccent} aria-hidden />
        <div className={styles.decoBlobBottom} aria-hidden />

        <header className={styles.visionHeading}>
          <p className={styles.sectionEyebrow}>Who We Are</p>
          <h2 className={styles.sectionTitle}>
            About <em className={styles.sectionTitleEm}>iGiTREE</em>
          </h2>
          <div className={styles.titleDivider}>
            <span className={styles.titleDividerLong} />
            <span className={styles.titleDividerShort} />
          </div>
          <p className={styles.visionHeadingDesc}>
            We connect families worldwide through technology, DNA discovery, and
            a deep respect for every heritage story.
          </p>
        </header>

        <div className={styles.visionInner}>
          {/* Left — image */}
          <div className={styles.imageCol}>
            <div className={styles.imageBorder} />
            <div className={styles.imageCard}>
              <div className={styles.imageCardInner}>
                <img
                  src={aboutImg}
                  alt="iGiTREE — heritage and family connections"
                  className={styles.aboutImage}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
            {/* Floating DNA badge */}
            <div className={styles.dnaBadge}>
              <span className={styles.dnaBadgeIcon}>🧬</span>
              <div>
                <p className={styles.dnaBadgeLine1}>DNA Matched</p>
                <p className={styles.dnaBadgeLine2}>10,000+ Families</p>
              </div>
            </div>
          </div>

          {/* Right — content */}
          <div className={styles.contentCol}>
            <div className={styles.infoBlock}>
              <div className={styles.infoBlockIcon}>🌍</div>
              <div>
                <h3 className={styles.infoBlockHeading}>Our Vision</h3>
                <p className={styles.infoBlockText}>
                  Our vision is a world where every person, regardless of their
                  background, can effortlessly trace their roots, reconnect with
                  loved ones, and preserve their unique family narratives.
                  Through innovative technology and a commitment to humanitarian
                  causes, we aspire to be the global leader in family unity, DNA
                  discovery, and ancestral heritage celebration.
                </p>
              </div>
            </div>

            <div className={styles.infoBlock}>
              <div className={styles.infoBlockIcon}>🎯</div>
              <div>
                <h3 className={styles.infoBlockHeading}>Our Mission</h3>
                <p className={styles.infoBlockText}>
                  At iGiTREE, our mission is to bridge the gaps of time and
                  distance, empowering individuals and communities to explore,
                  discover, and celebrate their family heritage. We're dedicated
                  to reuniting families, cultivating a sense of belonging, and
                  nurturing the world's most precious connections.
                </p>
              </div>
            </div>

            {/* Stats row */}
            <div className={styles.statsRow}>
              {[
                { value: "50K+", label: "Families Reunited" },
                { value: "120+", label: "Countries" },
                { value: "98%", label: "Satisfaction Rate" },
              ].map((s) => (
                <div key={s.label} className={styles.statItem}>
                  <span className={styles.statValue}>{s.value}</span>
                  <span className={styles.statLabel}>{s.label}</span>
                </div>
              ))}
            </div>

            <button className={styles.ctaBtn}>Get Started</button>
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className={styles.testimonialsSection}>
        <div className={styles.testimonialsDecoLeft} aria-hidden />
        <div className={styles.testimonialsDecoRight} aria-hidden />
        <TestimonialsSlider />
      </section>
    </div>
  );
}